import { useState, useCallback, useEffect, useRef } from 'react';
import { IPaymentCategory, IPendingChange, PendingStatusType } from 'constants/payment-categories.constants';
import {
	cloneCategories,
	findCategoryById,
	updateCategoryInTree,
	addCategoryToTree,
	getAllDescendantIds,
	updateMultipleCategoriesInTree,
} from 'utils/payment-category-tree.utils';

/**
 * Custom hook to manage pending changes for batch save functionality
 */
export const usePendingChanges = (originalCategories: IPaymentCategory[], suppressSync = false) => {
	const [pendingChanges, setPendingChanges] = useState<IPendingChange[]>([]);
	const [workingCategories, setWorkingCategories] = useState<IPaymentCategory[]>([]);

	// Ref-based counter for generating temporary IDs for new categories
	const tempIdCounterRef = useRef(-1);
	const getNextTempId = useCallback(() => {
		tempIdCounterRef.current -= 1;
		return tempIdCounterRef.current;
	}, []);

	// Initialize working categories from original (skip when suppressed)
	useEffect(() => {
		if (suppressSync) return;
		setWorkingCategories(cloneCategories(originalCategories));
		setPendingChanges([]);
		tempIdCounterRef.current = -1;
	}, [originalCategories, suppressSync]);

	// Track if there are pending changes
	const hasPendingChanges = pendingChanges.length > 0;
	const pendingChangesCount = pendingChanges.length;

	/**
	 * Add a create change
	 */
	const addCreate = useCallback(
		(parentId: number | null, name: string) => {
			const tempId = getNextTempId();
			const newCategory: IPaymentCategory = {
				id: tempId,
				name,
				parentId,
				isActive: true,
				children: [],
			};

			// Add to working categories
			setWorkingCategories((prev) => addCategoryToTree(prev, parentId, newCategory));

			// Record the pending change
			setPendingChanges((prev) => [
				...prev,
				{
					type: 'create',
					categoryId: tempId,
					parentId,
					name,
				},
			]);
		},
		[getNextTempId]
	);

	/**
	 * Add an update change
	 */
	const addUpdate = useCallback(
		(categoryId: number, name: string) => {
			// Find the original value if it's an existing category
			const originalCategory = findCategoryById(originalCategories, categoryId);

			// Update in working categories
			setWorkingCategories((prev) => updateCategoryInTree(prev, categoryId, { name }));

			// Check if reverting to original value
			if (originalCategory && originalCategory.name === name) {
				// Remove pending change if exists (reverted to original)
				setPendingChanges((prev) => prev.filter((change) => change.categoryId !== categoryId));
				return;
			}

			// Check if there's already a pending change for this category
			setPendingChanges((prev) => {
				const existingIndex = prev.findIndex((change) => change.categoryId === categoryId);

				if (existingIndex >= 0) {
					// Update existing pending change
					const updated = [...prev];
					if (updated[existingIndex].type === 'create') {
						// If it's a create, just update the name
						updated[existingIndex] = { ...updated[existingIndex], name };
					} else {
						// Otherwise update the change
						updated[existingIndex] = {
							...updated[existingIndex],
							type: 'update',
							name,
						};
					}
					return updated;
				}

				// Add new pending change
				return [
					...prev,
					{
						type: 'update',
						categoryId,
						name,
						originalValue: originalCategory || undefined,
					},
				];
			});
		},
		[originalCategories]
	);

	/**
	 * Add a toggle active change (cascades to children when deactivating)
	 */
	const addToggle = useCallback(
		(categoryId: number, isActive: boolean) => {
			// Find the category in working categories to get its descendants
			const category = findCategoryById(workingCategories, categoryId);
			if (!category) return;

			// If deactivating, cascade to all descendants
			// If activating, only toggle the specific category
			const idsToToggle = isActive ? [categoryId] : getAllDescendantIds(category);

			// Update all affected categories in working tree
			setWorkingCategories((prev) => updateMultipleCategoriesInTree(prev, idsToToggle, { isActive }));

			// Process pending changes for each affected category
			setPendingChanges((prev) => {
				let updated = [...prev];

				idsToToggle.forEach((id) => {
					const originalCategory = findCategoryById(originalCategories, id);

					// Check if reverting to original value
					if (originalCategory && originalCategory.isActive === isActive) {
						// Remove pending change if exists (reverted to original)
						updated = updated.filter((change) => change.categoryId !== id);
						return;
					}

					// Check if there's already a pending change for this category
					const existingIndex = updated.findIndex((change) => change.categoryId === id);

					if (existingIndex >= 0) {
						const existing = updated[existingIndex];
						if (existing.type === 'create') {
							// If it's a create, just update the isActive
							updated[existingIndex] = { ...existing, isActive };
						} else {
							// For other types, add/update the toggle
							updated[existingIndex] = { ...existing, isActive };
						}
					} else {
						// Add new pending change
						updated.push({
							type: 'toggle',
							categoryId: id,
							isActive,
							originalValue: originalCategory || undefined,
						});
					}
				});

				return updated;
			});
		},
		[originalCategories, workingCategories]
	);

	/**
	 * Clear all pending changes and reset to original
	 */
	const clearAll = useCallback(() => {
		setPendingChanges([]);
		setWorkingCategories(cloneCategories(originalCategories));
		// Reset temp ID counter
		tempIdCounterRef.current = -1;
	}, [originalCategories]);

	/**
	 * Get pending status for a category
	 */
	const getPendingStatus = useCallback(
		(categoryId: number): PendingStatusType => {
			const change = pendingChanges.find((c) => c.categoryId === categoryId);
			if (!change) return null;

			switch (change.type) {
				case 'create':
					return 'new';
				case 'update':
				case 'toggle':
					return 'modified';
				default:
					return null;
			}
		},
		[pendingChanges]
	);

	/**
	 * Get all pending changes for saving
	 */
	const getChangesToSave = useCallback(() => {
		return pendingChanges;
	}, [pendingChanges]);

	return {
		workingCategories,
		pendingChanges,
		hasPendingChanges,
		pendingChangesCount,
		addCreate,
		addUpdate,
		addToggle,
		clearAll,
		getPendingStatus,
		getChangesToSave,
	};
};
